import { useEffect, useState } from "react";
import type { UserRow } from "../lib/api";
import { listAdminUsers, type AdminUserRow } from "../lib/api";
import { AuthButton } from "./AuthButton";

interface Props {
  user: UserRow;
}

function fmtDate(ms: number | null | undefined): string {
  if (!ms) return "—";
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toISOString().replace("T", " ").slice(0, 16);
}

export function AdminDashboard({ user }: Props): JSX.Element {
  const [rows, setRows] = useState<AdminUserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { users } = await listAdminUsers();
        if (!cancelled) setRows(users);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = (rows ?? []).filter((r) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (r.email ?? "").toLowerCase().includes(q) ||
      (r.name ?? "").toLowerCase().includes(q) ||
      (r.role ?? "").toLowerCase().includes(q) ||
      (r.level ?? "").toLowerCase().includes(q)
    );
  });

  const authedCount = (rows ?? []).filter((r) => !r.is_anonymous).length;
  const anonCount = (rows ?? []).filter((r) => r.is_anonymous).length;

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 text-ink-100">
      <header className="flex items-center justify-between px-6 py-4 border-b border-ink-700/60">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-lime-400">Admin</div>
          <h1 className="text-lg font-semibold">Panel administratora</h1>
          <div className="text-xs text-ink-400 mt-0.5">
            zalogowany jako {user.email ?? user.name ?? user.id}
          </div>
        </div>
        <AuthButton variant="header" />
      </header>

      <div className="px-6 py-4 flex items-center gap-4 text-xs text-ink-300">
        <div className="bg-ink-800/60 border border-ink-700 rounded-lg px-3 py-2">
          Łącznie: <span className="text-ink-100 font-semibold">{rows?.length ?? "…"}</span>
        </div>
        <div className="bg-ink-800/60 border border-ink-700 rounded-lg px-3 py-2">
          Zalogowani: <span className="text-ink-100 font-semibold">{rows ? authedCount : "…"}</span>
        </div>
        <div className="bg-ink-800/60 border border-ink-700 rounded-lg px-3 py-2">
          Anonimowi: <span className="text-ink-100 font-semibold">{rows ? anonCount : "…"}</span>
        </div>
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="szukaj: email / imię / rola / poziom"
          className="ml-auto bg-ink-800/60 border border-ink-700 rounded-lg px-3 py-2 text-ink-100 placeholder-ink-500 focus:outline-none focus:border-lime-500 w-80"
        />
      </div>

      <div className="flex-1 overflow-auto px-6 pb-6">
        {error && (
          <div className="bg-red-900/40 border border-red-700 text-red-200 rounded-xl px-4 py-2 text-sm mb-3">
            {error}
          </div>
        )}
        {!rows && !error && <div className="text-ink-400 text-sm">Ładowanie…</div>}
        {rows && (
          <div className="overflow-hidden rounded-xl border border-ink-700/60">
            <table className="w-full text-sm">
              <thead className="bg-ink-800/80 text-ink-400 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Imię</th>
                  <th className="text-left px-3 py-2">Rola</th>
                  <th className="text-left px-3 py-2">Poziom</th>
                  <th className="text-left px-3 py-2">Plan</th>
                  <th className="text-left px-3 py-2">Typ</th>
                  <th className="text-left px-3 py-2">Ostatnio</th>
                  <th className="text-left px-3 py-2">Utworzono</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-ink-700/40 hover:bg-ink-800/40">
                    <td className="px-3 py-2 text-ink-100">{r.email ?? "—"}</td>
                    <td className="px-3 py-2 text-ink-200">{r.name ?? "—"}</td>
                    <td className="px-3 py-2 text-ink-300">{r.role}</td>
                    <td className="px-3 py-2 text-ink-300">{r.level ?? "—"}</td>
                    <td className="px-3 py-2 text-ink-300">{r.plan}</td>
                    <td className="px-3 py-2 text-ink-300">
                      {r.is_anonymous ? "anon" : "kc"}
                    </td>
                    <td className="px-3 py-2 text-ink-400 whitespace-nowrap">{fmtDate(r.last_seen_at)}</td>
                    <td className="px-3 py-2 text-ink-400 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-ink-500">
                      Brak wyników.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
